/** Domyślna paleta atramentu (house) + persystencja użytkownika w localStorage. */

export const DEFAULT_INK_SWATCHES = [
  "#281f1c",
  "#b5614e",
  "#5c4033",
  "#3f4a3c",
  "#ffffff",
  "#f3efe6",
  "#f8f5ef",
  "#d4a017",
  "#1f3a5f",
] as const;

export const INK_SWATCHES_STORAGE_KEY = "kz-studio-ink-swatches";
export const MAX_INK_SWATCHES = 24;

export function normalizeHex(value?: string, fallback = "#281f1c"): string {
  const raw = String(value || fallback).trim();
  return /^#[0-9a-fA-F]{6}$/.test(raw) ? raw.toLowerCase() : fallback.toLowerCase();
}

function uniqHex(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of list) {
    const hex = normalizeHex(item, "");
    if (!hex || seen.has(hex)) continue;
    seen.add(hex);
    out.push(hex);
  }
  return out;
}

export function loadInkSwatches(
  storageKey = INK_SWATCHES_STORAGE_KEY,
  defaults: readonly string[] = DEFAULT_INK_SWATCHES,
): string[] {
  if (typeof window === "undefined") return [...defaults];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [...defaults];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [...defaults];
    const cleaned = uniqHex(parsed.map(String)).slice(0, MAX_INK_SWATCHES);
    return cleaned.length ? cleaned : [...defaults];
  } catch {
    return [...defaults];
  }
}

export function saveInkSwatches(
  swatches: string[],
  storageKey = INK_SWATCHES_STORAGE_KEY,
): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify(uniqHex(swatches).slice(0, MAX_INK_SWATCHES)),
    );
  } catch {
    /* quota / private mode */
  }
}

export function addInkSwatch(swatches: string[], hex: string): string[] {
  return uniqHex([...swatches, hex]).slice(0, MAX_INK_SWATCHES);
}

export function removeInkSwatch(
  swatches: string[],
  hex: string,
  defaults: readonly string[] = DEFAULT_INK_SWATCHES,
): string[] {
  const target = normalizeHex(hex);
  const next = swatches.filter((c) => normalizeHex(c) !== target);
  return next.length ? next : [...defaults];
}

export function isLightInk(hex: string): boolean {
  const h = normalizeHex(hex);
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  // Perceived luminance; jasne swatche potrzebują mocniejszej ramki.
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.82;
}
